/**
 * GraphSearch — type-ahead search for a node by label. On select, focuses
 * the node and highlights its 1-hop neighborhood (reuses the existing
 * `focusOnNode` renderer handle — no new highlight machinery).
 *
 * Why: an attack-surface graph of 50+ nodes makes "where the hell is
 * `pphosted.com`?" a real pain. Ctrl+F lands the cursor in the search,
 * typing filters the match list, Enter focuses the top hit.
 *
 * Match rule: case-insensitive substring on `label`, falling back to `type`
 * for short queries (so typing `app` reveals every application node).
 * Ranked by exact-prefix > prefix > substring.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ElementDefinition } from 'cytoscape';

interface Props {
  /** Full element set — used to find candidates even when type-filtered. */
  elements: ElementDefinition[];
  /** Subset currently rendered. A search hit that's NOT in this set is
   * "currently hidden" (type-filter or per-node hide); the hit list badges
   * it and `onFocusNode` calls `onUnhide` first so focusing actually works. */
  visibleElements: ElementDefinition[];
  onFocusNode: (nodeId: string) => void;
  /** Called before focusing a hidden hit so the focus call sees the node. */
  onUnhideForSearch?: (nodeId: string) => void;
}

interface NodeHit {
  id: string;
  label: string;
  type: string;
  /** Lower = better. */
  score: number;
  /** True when the node is in `elements` but NOT in `visibleElements` (the
   * type-filter or per-node hide dropped it from the render). The result row
   * gets a "hidden" badge and clicking it unhides before focusing. */
  hidden: boolean;
}

/**
 * Rank a candidate node against the query. Lower score = better match.
 * Returns null when the node doesn't match at all.
 */
function scoreMatch(label: string, type: string, q: string): number | null {
  const li = label.toLowerCase();
  const ti = type.toLowerCase();
  if (li === q) return 0;
  if (li.startsWith(q)) return 1;
  if (li.includes(q)) return 2;
  if (ti === q) return 3;
  if (ti.startsWith(q)) return 4;
  if (ti.includes(q)) return 5;
  return null;
}

const MAX_RESULTS = 12;

/** Module-level singleton so multiple GraphSearch instances coordinate the
 *  global Ctrl/Cmd+F intercept. Whichever instance "owns" the listener wins;
 *  others stay dormant. */
let activeSearchInstance: symbol | null = null;

export function GraphSearch({ elements, visibleElements, onFocusNode, onUnhideForSearch }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const instanceIdRef = useRef<symbol | null>(null);
  if (instanceIdRef.current === null) instanceIdRef.current = Symbol('GraphSearch');

  // Global Ctrl/Cmd+F handler — only takes over when the graph panel is
  // focused or no other text input is active, so it doesn't fight with
  // browser find inside the chat composer.
  useEffect(() => {
    // Claim the global Ctrl+F intercept opportunistically. Each instance
    // tries to claim on mount; the claim is re-evaluated on every keydown
    // so an instance that mounted while another was active still gets the
    // hotkey if that other instance has since unmounted. Without this
    // re-evaluation, a B-then-A unmount order leaves the hotkey unowned
    // and Ctrl+F goes dead even though B is still listening.
    const id = instanceIdRef.current!;
    if (activeSearchInstance === null) activeSearchInstance = id;
    const onKey = (e: KeyboardEvent) => {
      if (activeSearchInstance === null) activeSearchInstance = id; // claim if vacant
      if (activeSearchInstance !== id) return; // another instance owns the hotkey
      const isFind = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f';
      if (!isFind) return;
      const tag = (document.activeElement as HTMLElement | null)?.tagName ?? '';
      // Don't hijack browser find while the user is typing in another input.
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      setOpen(true);
      // microtask: let React mount the input before focusing it.
      queueMicrotask(() => inputRef.current?.focus());
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (activeSearchInstance === id) activeSearchInstance = null;
    };
  }, []);

  const visibleIds = useMemo(() => {
    const set = new Set<string>();
    for (const el of visibleElements) {
      const isNode = el.group === 'nodes' || (!el.group && !el.data.source);
      if (isNode) set.add(el.data.id as string);
    }
    return set;
  }, [visibleElements]);

  const hits = useMemo<NodeHit[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const matches: NodeHit[] = [];
    for (const el of elements) {
      const isNode = el.group === 'nodes' || (!el.group && !el.data.source);
      if (!isNode) continue;
      const id = el.data.id as string;
      const label = (el.data.label as string) ?? id;
      const type = (el.data.type as string) ?? '';
      const score = scoreMatch(label, type, q);
      if (score !== null) matches.push({ id, label, type, score, hidden: !visibleIds.has(id) });
    }
    matches.sort((a, b) => a.score - b.score || a.label.localeCompare(b.label));
    return matches.slice(0, MAX_RESULTS);
  }, [elements, visibleIds, query]);

  // Reset active selection whenever the hit set changes — the previously-
  // active index could now point at a different (or no) hit.
  useEffect(() => {
    setActiveIdx(0);
  }, [hits]);

  const focusHit = useCallback((hit: NodeHit) => {
    // If the hit is currently hidden (type-filter dropped its type, or the
    // node was right-click-hidden), unhide first so `focusOnNode` actually
    // sees a node to focus. Without this, search hits for hidden nodes
    // silently no-op and the user sees nothing happen.
    if (hit.hidden) onUnhideForSearch?.(hit.id);
    onFocusNode(hit.id);
    setOpen(false);
    setQuery('');
  }, [onFocusNode, onUnhideForSearch]);

  const onInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery('');
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(hits.length - 1, 0)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter' && hits[activeIdx]) {
      e.preventDefault();
      focusHit(hits[activeIdx]!);
    }
  }, [hits, activeIdx, focusHit]);

  if (!open) {
    return (
      <button
        className="topbar-icon-btn graph-search-btn"
        onClick={() => { setOpen(true); queueMicrotask(() => inputRef.current?.focus()); }}
        title="Search nodes (Ctrl+F)"
        aria-label="Search nodes"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </button>
    );
  }

  return (
    <div className="graph-search-wrapper">
      <div className="graph-search-input-row">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="graph-search-icon">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={inputRef}
          className="graph-search-input"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKeyDown}
          placeholder="Find node…"
          autoFocus
        />
        <button
          className="graph-search-close"
          onClick={() => { setOpen(false); setQuery(''); }}
          title="Close search (Esc)"
          aria-label="Close search"
        >
          ×
        </button>
      </div>
      {query.trim() && (
        <div className="graph-search-results">
          {hits.length === 0 ? (
            <div className="graph-search-empty">No matches.</div>
          ) : (
            hits.map((hit, i) => (
              <button
                key={hit.id}
                className={`graph-search-result ${i === activeIdx ? 'active' : ''} ${hit.hidden ? 'is-hidden-hit' : ''}`}
                onClick={() => focusHit(hit)}
                onMouseEnter={() => setActiveIdx(i)}
                title={hit.hidden ? 'Currently hidden — clicking will restore + focus' : undefined}
              >
                <span className={`node-popover-type type-${hit.type}`}>{hit.type}</span>
                <span className="graph-search-result-label">{hit.label}</span>
                {hit.hidden && <span className="graph-search-hidden-badge">hidden</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
